import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FileText, Plus, Trash2, Save, X, RefreshCw, Eye, EyeOff, ArrowUp, ArrowDown, ExternalLink } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import RichTextEditor from './RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400';
const slugify = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

const PageForm = ({ editing, setEditing, onSave, saving, error }) => {
  const upd = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setEditing(null)}>
      <div data-testid="page-form-modal" className="my-10 w-full max-w-3xl bg-white dark:bg-[#141416] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="font-serif text-[18px] text-neutral-900 dark:text-neutral-100">{editing.isNew ? 'New page' : 'Edit page'}</h3>
          <button onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] tracking-widest uppercase text-neutral-500">Title (menu label)</span>
              <input data-testid="page-title-input" className={inputCls} value={editing.title} maxLength={80}
                onChange={(e) => { const t = e.target.value; setEditing(prev => ({ ...prev, title: t, slug: prev.slugTouched ? prev.slug : slugify(t) })); }} />
            </label>
            <label className="block">
              <span className="text-[11px] tracking-widest uppercase text-neutral-500">URL slug</span>
              <div className="flex items-center">
                <span className="text-[12.5px] text-neutral-400 mr-1">/page/</span>
                <input data-testid="page-slug-input" className={inputCls + ' font-mono'} value={editing.slug}
                  onChange={(e) => setEditing(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''), slugTouched: true }))} />
              </div>
            </label>
          </div>
          <div>
            <span className="block text-[11px] tracking-widest uppercase text-neutral-500 mb-1">Content</span>
            <RichTextEditor value={editing.content} onChange={(html) => upd('content', html)} />
          </div>
          <label className="inline-flex items-center gap-2 text-[13px]">
            <input data-testid="page-published-checkbox" type="checkbox" checked={!!editing.is_published} onChange={(e) => upd('is_published', e.target.checked)} className="w-4 h-4 accent-emerald-600" />
            Published (visible in the website header)
          </label>
          {error && <div data-testid="page-form-error" className="rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-3 py-2 text-[12.5px] text-red-700 dark:text-red-300">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-neutral-200 dark:border-neutral-800">
          <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 text-[13.5px]">Cancel</button>
          <button data-testid="page-save-btn" onClick={onSave} disabled={saving || !editing.title.trim() || !editing.slug} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const PagesPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await axios.get(`${API}/admin/pages`, { headers })).data); }
    catch (e) { setMsg('Failed to load: ' + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const patch = (id, body) => axios.patch(`${API}/admin/pages/${id}`, body, { headers });

  const save = async () => {
    setSaving(true); setMsg('');
    const body = { title: editing.title, slug: editing.slug, content: editing.content || '', is_published: !!editing.is_published };
    try {
      if (editing.isNew) await axios.post(`${API}/admin/pages`, { ...body, sort_order: rows.length }, { headers });
      else await patch(editing.id, body);
      setEditing(null); await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const move = async (idx, dir) => {
    const next = [...rows];
    const j = idx + dir;
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next);
    try { await Promise.all(next.map((p, i) => (p.sort_order !== i ? patch(p.id, { sort_order: i }) : null))); await load(); }
    catch { setMsg('Reorder failed'); load(); }
  };

  const toggle = async (p) => { try { await patch(p.id, { is_published: !p.is_published }); await load(); } catch { setMsg('Update failed'); } };
  const remove = async (p) => {
    if (!window.confirm(`Delete page "${p.title}"?`)) return;
    try { await axios.delete(`${API}/admin/pages/${p.id}`, { headers }); await load(); } catch { setMsg('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="pages-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><FileText size={20} className="text-neutral-400" /> Pages</h2>
          <p className="text-[12.5px] text-neutral-500">Published pages appear in the website header in this order.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px]"><RefreshCw size={13} /> Refresh</button>
          <button data-testid="add-page-btn" onClick={() => { setMsg(''); setEditing({ isNew: true, title: '', slug: '', content: '', is_published: true }); }} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold"><Plus size={14} /> New page</button>
        </div>
      </div>
      {msg && !editing && <div className="text-red-600 text-[13px]" data-testid="pages-error">{msg}</div>}

      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] divide-y divide-neutral-100 dark:divide-neutral-800">
        {loading ? <div className="p-6 text-center text-neutral-500 text-[13.5px]">Loading…</div>
          : rows.length === 0 ? <div className="p-6 text-center text-neutral-500 text-[13.5px]">No pages yet.</div>
          : rows.map((p, i) => (
            <div key={p.id} data-testid={`page-row-${p.slug}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex flex-col">
                <button data-testid={`page-up-${p.slug}`} disabled={i === 0} onClick={() => move(i, -1)} className="p-0.5 text-neutral-400 hover:text-neutral-800 disabled:opacity-30"><ArrowUp size={13} /></button>
                <button data-testid={`page-down-${p.slug}`} disabled={i === rows.length - 1} onClick={() => move(i, 1)} className="p-0.5 text-neutral-400 hover:text-neutral-800 disabled:opacity-30"><ArrowDown size={13} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[14px] text-neutral-900 dark:text-neutral-100 truncate">{p.title}</div>
                <div className="text-[11.5px] text-neutral-500 font-mono truncate">/page/{p.slug}</div>
              </div>
              <button data-testid={`page-toggle-${p.slug}`} onClick={() => toggle(p)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${p.is_published ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}`}>
                {p.is_published ? <Eye size={11} /> : <EyeOff size={11} />} {p.is_published ? 'Published' : 'Hidden'}
              </button>
              {p.is_published && <a data-testid={`page-view-${p.slug}`} href={`/page/${p.slug}`} target="_blank" rel="noreferrer" className="p-1.5 text-neutral-500 hover:text-emerald-600"><ExternalLink size={14} /></a>}
              <button data-testid={`page-edit-${p.slug}`} onClick={() => { setMsg(''); setEditing({ ...p, isNew: false, slugTouched: true }); }} className="px-2.5 py-1 text-[12px] rounded-md border border-neutral-200 dark:border-neutral-700">Edit</button>
              <button data-testid={`page-delete-${p.slug}`} onClick={() => remove(p)} className="p-1.5 rounded-md text-neutral-500 hover:text-red-500"><Trash2 size={14} /></button>
            </div>
          ))}
      </div>

      {editing && <PageForm editing={editing} setEditing={(v) => { setEditing(v); if (!v) setMsg(''); }} onSave={save} saving={saving} error={msg} />}
    </div>
  );
};

export default PagesPanel;
